import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Image from 'next/image';
import ProjectEstimator from './ProjectEstimator';

const PricingTiers = () => {
  const [isEstimatorOpen, setIsEstimatorOpen] = useState(false);

  return (
    <section className="bg-zinc-900 py-16 sm:py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Pricing{' '}
            <span className="bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">
              Tiers
            </span>
          </h2>
          <p className="text-lg sm:text-xl text-stone-300 max-w-3xl mx-auto">
            Explore our Price Tiers of the products we craft for Hawaii's businesses.
            <br className="hidden sm:block" />
            <span className="text-stone-400 text-sm sm:text-base mt-2 block">
              Note: All quotes will be finalized based on complexity, time, and landscape of project
              proposal. Minimums enforced.
            </span>
          </p>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-8 flex justify-center"
          >
            <button
              onClick={() => setIsEstimatorOpen(true)}
              className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400 text-white font-bold py-3 px-8 rounded-full shadow-[0_0_20px_rgba(6,182,212,0.3)] hover:shadow-[0_0_30px_rgba(6,182,212,0.5)] transition-all transform hover:scale-105 flex items-center gap-2"
            >
              Try Our Project Estimator
            </button>
          </motion.div>
        </div>

        <div className="space-y-12">
          {/* Websites Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="border border-cyan-500/30 rounded-2xl p-4 sm:p-6 bg-zinc-900/50"
          >
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Left Panel */}
              <div className="lg:w-1/4 bg-black rounded-xl p-6 flex flex-col justify-end min-h-[300px] relative overflow-hidden border border-white/5 group">
                {/* Poster Background */}
                <Image
                  src="/images/seoServiceZumify.webp"
                  alt="Websites Tier Poster"
                  fill
                  className="object-cover opacity-60 group-hover:opacity-80 transition-opacity duration-500"
                />
                {/* Decorative Background for text readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent pointer-events-none" />
                <div className="absolute inset-0 opacity-40 bg-gradient-to-b from-cyan-500/20 to-transparent pointer-events-none mix-blend-overlay" />

                <div className="relative z-10 mt-auto">
                  <h3 className="text-2xl font-bold text-white mb-2">Websites</h3>
                  <div className="space-y-1">
                    <p className="text-sm text-stone-400">Starter Plan</p>
                    <p className="text-sm text-stone-400">Conversion Plan</p>
                    <p className="text-sm text-stone-400">Custom Plan</p>
                  </div>
                </div>
              </div>

              {/* Right Panel - Tiers */}
              <div className="lg:w-3/4 grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Tier 1 */}
                <div className="bg-zinc-800/50 rounded-xl p-6 border border-cyan-500/20 flex flex-col">
                  <h4 className="text-xl font-semibold text-cyan-400 mb-4 text-center">
                    Starter Plan
                  </h4>
                  <ul className="space-y-3 mb-8 flex-1">
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-cyan-500">•</span>
                      <span>Custom-designed, animated website (fully responsive)</span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-cyan-500">•</span>
                      <span>3-5 core pages (Home, About, Services, Contact)</span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-cyan-500">•</span>
                      <span>Basic SEO setup (meta tags, indexing, site structure)</span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-cyan-500">•</span>
                      <span>Standard integrations (email, forms, scheduling like Calendly)</span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-cyan-500">•</span>
                      <span>Redesign Service for Wix, Sqaurespace, Shopify, Wordpress</span>
                    </li>
                  </ul>
                  <div className="text-center pt-4 border-t border-white/10 space-y-4">
                    <div>
                      <p className="text-sm text-stone-400 mb-1">Hourly Rate</p>
                      <p className="text-xl font-bold text-cyan-400">$75 / hr</p>
                      <div className="mt-2 space-y-1">
                        <p className="text-s text-stone-400">Average Estimated Hours: <span className="text-stone-300">20-50</span></p>
                        <p className="text-s text-stone-400">Estimated Investment: <br/> <span className="text-cyan-400">$1,500 - $3,750</span></p>
                      </div>
                    </div>
                    <div className="pt-4 border-t border-white/10">
                      <p className="text-sm text-stone-400 mb-1">Maintenance</p>
                      <p className="text-lg font-bold text-white">Starter Plan</p>
                      <p className="text-cyan-400">$49.99 / mo</p>
                    </div>
                  </div>
                </div>

                {/* Tier 2 */}
                <div className="bg-zinc-800/50 rounded-xl p-6 border border-purple-500/20 flex flex-col">
                  <h4 className="text-xl font-semibold text-purple-400 mb-4 text-center">
                    Conversion Plan
                  </h4>
                  <ul className="space-y-3 mb-8 flex-1">
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-purple-500">•</span>
                      <span>Conversion-focused UX/UI (built around user behavior + funnels)</span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-purple-500">•</span>
                      <span>Full analytics + event tracking (clicks, forms, conversions)</span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-purple-500">•</span>
                      <span>SEO strategy plan (keyword targeting + content direction)</span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-purple-500">•</span>
                      <span>AI-driven research (competitors + search intent)</span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-purple-500">•</span>
                      <span>Landing page structuring (CTAs, copy flow, user journey)</span>
                    </li>
                  </ul>
                  <div className="text-center pt-4 border-t border-white/10 space-y-4">
                    <div>
                      <p className="text-sm text-stone-400 mb-1">Flexible Development Pricing</p>
                      <p className="text-xl font-bold text-purple-400">$90 / hr</p>
                      <div className="mt-2 space-y-1">
                        <p className="text-s text-stone-400">Average Estimated Hours: <span className="text-stone-300">50-75</span></p>
                        <p className="text-s text-stone-400">Estimated Investment: <br/> <span className="text-purple-400">$4,500 - $6,750</span></p>
                      </div>
                    </div>
                    <div className="pt-4 border-t border-white/10">
                      <p className="text-sm text-stone-400 mb-1">Maintenance</p>
                      <p className="text-lg font-bold text-white">Growth Plan</p>
                      <p className="text-purple-400">$199.99 / mo</p>
                    </div>
                  </div>
                </div>

                {/* Tier 3 */}
                <div className="bg-zinc-800/50 rounded-xl p-6 border border-pink-500/20 flex flex-col">
                  <h4 className="text-xl font-semibold text-pink-400 mb-4 text-center">
                    Custom Plan
                  </h4>
                  <ul className="space-y-3 mb-8 flex-1">
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-pink-500">•</span>
                      <span>
                        Fully custom software built around your business workflow. 
                      </span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-pink-500">•</span>
                      <span>
                        Secure user authhentication, employee/admin dashboards, and client portals.
                      </span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-pink-500">•</span>
                      <span>Custom database architecture for storing and managing business data.</span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-pink-500">•</span>
                      <span>API integrations (Stripe, QuickBooks, CRMs, Google, third-party platforms)</span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-pink-500">•</span>
                      <span>Scalable architecture deisgned to grow your business</span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-pink-500">•</span>
                      <span>Role based permissions and account management</span>
                    </li>
                  </ul>
                  <div className="text-center pt-4 border-t border-white/10 space-y-4">
                    <div>
                      <p className="text-sm text-stone-400 mb-1">Flexible Development Pricing</p>
                      <p className="text-xl font-bold text-pink-400">$150 / hr</p>
                      <div className="mt-2 space-y-1">
                        <p className="text-s text-stone-400">Average Estimated Hours: <span className="text-stone-300">80+</span></p>
                        <p className="text-s text-stone-400">Estimated Investment: <br/> <span className="text-pink-400">$12,000+</span></p>
                      </div>
                    </div>
                    <div className="pt-4 border-t border-white/10">
                      <p className="text-sm text-stone-400 mb-1">Maintenance</p>
                      <p className="text-lg font-bold text-white">Premium Plan</p>
                      <p className="text-pink-400">$499.99 / mo</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>


          </motion.div>

          {/* Mobile Apps Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="border border-rose-500/30 rounded-2xl p-4 sm:p-6 bg-zinc-900/50"
          >
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Left Panel */}
              <div className="lg:w-1/4 bg-black rounded-xl p-6 flex flex-col justify-end min-h-[300px] relative overflow-hidden border border-white/5 group">
                {/* Poster Background */}
                <Image
                  src="/images/SpibeSparks.webp"
                  alt="Mobile Apps Tier Poster"
                  fill
                  className="object-cover opacity-60 group-hover:opacity-80 transition-opacity duration-500"
                />
                {/* Decorative Background for text readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent pointer-events-none" />
                <div className="absolute inset-0 opacity-40 bg-gradient-to-b from-rose-500/20 to-transparent pointer-events-none mix-blend-overlay" />

                <div className="relative z-10 mt-auto">
                  <h3 className="text-2xl font-bold text-white mb-2">Mobile Apps</h3>
                  <div className="space-y-1">
                    <p className="text-sm text-stone-400">Mobile Launch</p>
                    <p className="text-sm text-stone-400">Mobile Growth</p>
                    <p className="text-sm text-stone-400">Mobile Enterprise</p>
                  </div>
                </div>
              </div>

              {/* Right Panel - Tiers */}
              <div className="lg:w-3/4 grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Tier 1 */}
                <div className="bg-zinc-800/50 rounded-xl p-6 border border-cyan-500/20 flex flex-col">
                  <h4 className="text-xl font-semibold text-cyan-400 mb-4 text-center">Mobile Launch</h4>
                  <ul className="space-y-3 mb-8 flex-1">
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-cyan-500">•</span>
                      <span>
                        Custom-designed mobile experience (IOS build + Android build)
                      </span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-cyan-500">•</span>
                      <span>3-6 core screens focused on the single main use case</span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-cyan-500">•</span>
                      <span>Basic frontend + lightweight backend (just enough to function)</span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-cyan-500">•</span>
                      <span>Simple data handling (basic storage or minimal database)</span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-cyan-500">•</span>
                      <span>Core feature only (no extras, no complexity)</span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-cyan-500">•</span>
                      <span>Built for iteration (clean, scalable starting structure)</span>
                    </li>
                  </ul>
                  <div className="text-center pt-4 border-t border-white/10 space-y-4">
                    <div>
                      <p className="text-sm text-stone-400 mb-1">Flexible Development Pricing</p>
                      <p className="text-xl font-bold text-cyan-400">$90 / hr</p>
                      <div className="mt-2 space-y-1">
                        <p className="text-s text-stone-400">Average Estimated Hours: <span className="text-stone-300">35-55</span></p>
                        <p className="text-s text-stone-400">Estimated Investment: <br/> <span className="text-cyan-400">$3,100–$4,950</span></p>
                      </div>
                    </div>
                    <div className="pt-4 border-t border-white/10">
                      <p className="text-sm text-stone-400 mb-1">Maintenance</p>
                      <p className="text-lg font-bold text-white">Starter Plan</p>
                      <p className="text-cyan-400">$299.99 / Month</p>
                    </div>
                  </div>
                </div>

                {/* Tier 2 */}
                <div className="bg-zinc-800/50 rounded-xl p-6 border border-purple-500/20 flex flex-col">
                  <h4 className="text-xl font-semibold text-purple-400 mb-4 text-center">
                    Mobile Growth
                  </h4>
                  <ul className="space-y-3 mb-8 flex-1">
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-purple-500">•</span>
                      <span>Frontend + backend architecture </span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-purple-500">•</span>
                      <span>Scalable updates and maintenance </span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-purple-500">•</span>
                      <span>Complete UX/UI polish (transitions, states, error handling)</span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-purple-500">•</span>
                      <span>Structured database implementation</span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-purple-500">•</span>
                      <span>Core integrations (auth, basic APIs, notifications if needed)</span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-purple-500">•</span>
                      <span>Scalable infrastructure (handles real users)</span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-purple-500">•</span>
                      <span>Unlimited feature requests (1 active at a time)</span>
                    </li>
                  </ul>
                  <div className="text-center pt-4 border-t border-white/10 space-y-4">
                    <div>
                      <p className="text-sm text-stone-400 mb-1">Flexible Development Pricing</p>
                      <p className="text-xl font-bold text-purple-400">$110 / hr</p>
                      <div className="mt-2 space-y-1">
                        <p className="text-s text-stone-400">Average Estimated Hours: <span className="text-stone-300">60-100</span></p>
                        <p className="text-s text-stone-400">Estimated Investment: <br/> <span className="text-purple-400">$6,600-$11,000</span></p>
                      </div>
                    </div>
                    <div className="pt-4 border-t border-white/10">
                      <p className="text-sm text-stone-400 mb-1">Maintenance</p>
                      <p className="text-lg font-bold text-white">Growth Plan</p>
                      <p className="text-purple-400">$1,499.99 / month</p>
                    </div>
                  </div>
                </div>

                {/* Tier 3 */}
                <div className="bg-zinc-800/50 rounded-xl p-6 border border-pink-500/20 flex flex-col">
                  <h4 className="text-xl font-semibold text-pink-400 mb-4 text-center">
                    Mobile Enterprise
                  </h4>
                  <ul className="space-y-3 mb-8 flex-1">
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-pink-500">•</span>
                      <span className="block">
                        Advanced systems:
                        <br />
                        - User authentication flows (multi-role, permissions)
                        <br />
                        - Payments / subscriptions
                        <br />
                        - Real-time features (chat, updates, syncing)
                        <br />- Dashboards (admin + user)
                      </span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-pink-500">•</span>
                      <span>Complex API integrations & third-party services</span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-pink-500">•</span>
                      <span>Performance optimization (speed, load handling, responsiveness)</span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-pink-500">•</span>
                      <span>Security hardening (data protection, auth layers)</span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-pink-500">•</span>
                      <span>Scalable backend architecture (cloud infra, load ready systems)</span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-pink-500">•</span>
                      <span>Ongoing development + growth support</span>
                    </li>
                  </ul>
                  <div className="text-center pt-4 border-t border-white/10 space-y-4">
                    <div>
                      <p className="text-sm text-stone-400 mb-1">Flexible Development Pricing</p>
                      <p className="text-xl font-bold text-pink-400">$160 / hr</p>
                      <div className="mt-2 space-y-1">
                        <p className="text-s text-stone-400">Average Estimated Hours: <span className="text-stone-300">250+</span></p>
                        <p className="text-s text-stone-400">Estimated Investment: <br/> <span className="text-pink-400">$40,000+</span></p>
                      </div>
                    </div>
                    <div className="pt-4 border-t border-white/10">
                      <p className="text-sm text-stone-400 mb-1">Maintenance</p>
                      <p className="text-lg font-bold text-white">Premium Plan</p>
                      <p className="text-pink-400">$2,999.99 / month</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>


          </motion.div>

          {/* AI Agents Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="border border-purple-500/30 rounded-2xl p-4 sm:p-6 bg-zinc-900/50"
          >
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Left Panel */}
              <div className="lg:w-1/4 bg-black rounded-xl p-6 flex flex-col justify-end min-h-[300px] relative overflow-hidden border border-white/5 group">
                {/* Poster Background */}
                <Image
                  src="/images/workingWebDesign.webp"
                  alt="Design & AI Agents Tier Poster"
                  fill
                  className="object-cover opacity-60 group-hover:opacity-80 transition-opacity duration-500"
                />
                {/* Decorative Background for text readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent pointer-events-none" />
                <div className="absolute inset-0 opacity-40 bg-gradient-to-b from-purple-500/20 to-transparent pointer-events-none mix-blend-overlay" />

                <div className="relative z-10 mt-auto">
                  <h3 className="text-2xl font-bold text-white mb-2">
                    Automated Agents
                  </h3>
                  <div className="space-y-1">
                    <p className="text-sm text-stone-400">Starter Agents</p>
                    <p className="text-sm text-stone-400">Advanced Agents</p>
                    <p className="text-sm text-stone-400">Enterprise Agents</p>
                  </div>
                </div>
              </div>

              {/* Right Panel - Tiers */}
              <div className="lg:w-3/4 grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Tier 1 */}
                <div className="bg-zinc-800/50 rounded-xl p-6 border border-cyan-500/20 flex flex-col">
                  <h4 className="text-xl font-semibold text-cyan-400 mb-4 text-center">
                    Starter Agents
                  </h4>
                  <ul className="space-y-3 mb-8 flex-1">
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-cyan-500">•</span>
                      <span>
                        Pre-built AI agent templates (chat, email response, reminders, simple tasks)
                      </span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-cyan-500">•</span>
                      <span>Integration with 1-2 platforms (e.g., Slack, Gmail, or web apps)</span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-cyan-500">•</span>
                      <span>Rule-based workflows with limited customization</span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-cyan-500">•</span>
                      <span>Basic analytics and performance tracking</span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-cyan-500">•</span>
                      <span>
                        Ideal for small businesses or teams starting to explore AI automation
                      </span>
                    </li>
                  </ul>
                  <div className="text-center pt-4 border-t border-white/10 space-y-4">
                    <div>
                      <p className="text-sm text-stone-400 mb-1">Flexible Development Pricing</p>
                      <p className="text-xl font-bold text-cyan-400">$100 / hr</p>
                      <div className="mt-2 space-y-1">
                        <p className="text-s text-stone-400">Average Estimated Hours: <span className="text-stone-300">10-20</span></p>
                        <p className="text-s text-stone-400">Estimated Investment: <br/> <span className="text-cyan-400">$1,000-$2,000</span></p>
                      </div>
                    </div>
                    <div className="pt-4 border-t border-white/10">
                      <p className="text-sm text-stone-400 mb-1">Maintenance</p>
                      <p className="text-lg font-bold text-white">Starter Plan</p>
                      <p className="text-cyan-400">$99.99 / month</p>
                    </div>
                  </div>
                </div>

                {/* Tier 2 */}
                <div className="bg-zinc-800/50 rounded-xl p-6 border border-purple-500/20 flex flex-col">
                  <h4 className="text-xl font-semibold text-purple-400 mb-4 text-center">
                    Advanced Agents
                  </h4>
                  <ul className="space-y-3 mb-8 flex-1">
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-purple-500">•</span>
                      <span>Fully customized AI agent logic for your business processes</span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-purple-500">•</span>
                      <span>
                        Integration with multiple platforms (CRM, marketing tools, internal apps)
                      </span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-purple-500">•</span>
                      <span>Advanced workflow automation, triggers, multi-step processes</span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-purple-500">•</span>
                      <span>Analytics, reporting, and performance optimization</span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-purple-500">•</span>
                      <span>
                        Scalable for teams, SaaS platforms, or enterprise-level operations
                      </span>
                    </li>
                  </ul>
                  <div className="text-center pt-4 border-t border-white/10 space-y-4">
                    <div>
                      <p className="text-sm text-stone-400 mb-1">Flexible Development Pricing</p>
                      <p className="text-xl font-bold text-purple-400">$125 / hr</p>
                      <div className="mt-2 space-y-1">
                        <p className="text-s text-stone-400">Average Estimated Hours: <span className="text-stone-300">25-40</span></p>
                        <p className="text-s text-stone-400">Estimated Investment: <br/> <span className="text-purple-400">$3,125-$5,000</span></p>
                      </div>
                    </div>
                    <div className="pt-4 border-t border-white/10">
                      <p className="text-sm text-stone-400 mb-1">Maintenance</p>
                      <p className="text-lg font-bold text-white">Growth Plan</p>
                      <p className="text-purple-400">$199.99 / month</p>
                    </div>
                  </div>
                </div>

                {/* Tier 3 */}
                <div className="bg-zinc-800/50 rounded-xl p-6 border border-pink-500/20 flex flex-col">
                  <h4 className="text-xl font-semibold text-pink-400 mb-4 text-center">
                    Enterprise Agents
                  </h4>
                  <ul className="space-y-3 mb-8 flex-1">
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-pink-500">•</span>
                      <span>Large-scale multi-agent systems and custom LLM deployment</span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-pink-500">•</span>
                      <span>
                        Complete enterprise software integration (ERP, custom databases, legacy systems)
                      </span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-pink-500">•</span>
                      <span>Advanced security, compliance features, and self-hosted options</span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-pink-500">•</span>
                      <span>Dedicated support and continuous workflow optimization</span>
                    </li>
                    <li className="text-sm text-stone-300 flex items-start">
                      <span className="mr-2 text-pink-500">•</span>
                      <span>
                        Built for large corporations requiring massive scale and complete control
                      </span>
                    </li>
                  </ul>
                  <div className="text-center pt-4 border-t border-white/10 space-y-4">
                    <div>
                      <p className="text-sm text-stone-400 mb-1">Flexible Development Pricing</p>
                      <p className="text-xl font-bold text-pink-400">$150 / hr</p>
                      <div className="mt-2 space-y-1">
                        <p className="text-s text-stone-400">Average Estimated Hours: <span className="text-stone-300">70+</span></p>
                        <p className="text-s text-stone-400">Estimated Investment: <br/> <span className="text-pink-400">$10,500+</span></p>
                      </div>
                    </div>
                    <div className="pt-4 border-t border-white/10">
                      <p className="text-sm text-stone-400 mb-1">Maintenance</p>
                      <p className="text-lg font-bold text-white">Premium Plan</p>
                      <p className="text-pink-400">$999.99 / month</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Project Estimator Modal */}
      <ProjectEstimator isOpen={isEstimatorOpen} onClose={() => setIsEstimatorOpen(false)} />
    </section>
  );
};

export default PricingTiers;
